// ═══════════════════════════════════════════════════════════════════════════════
// ATLAS DES POSTURES — Données statiques front
// Cartographie des manières d'être face à la complexité
//
// Architecture universaliste et agnostique :
// - 6 axes invariants (valables dans tous les contextes)
// - N archétypes comme positions dans cet espace
// - Scénarios génériques avec projections par domaine
// - Référentiels opaques (chaînes libres, PESACORE ne les interprète pas)
// - Traçabilité structurée, exportable, persistable localement
// ═══════════════════════════════════════════════════════════════════════════════

export const posturesVisualization3DData = {
  enabled: true,
  type: "postures",
  version: "1.0",
  target: "atlas_epistemologique",
  targetLabel: "Atlas des postures",
  targetSubtitle: "Cartographie des manières d'être face à la complexité",
  scaleUnit: "positional",

  camera: {
    initialPosition: { x: 0, y: 5, z: 28 },
    lookAt: { x: 0, y: 0, z: 0 },
    controls: "orbit",
  },

  ambiance: {
    background: "#06060f",
    grid: true,
    gridColor: "rgba(167,139,250,0.03)",
    particles: true,
    fog: { enabled: true, color: "#06060f", near: 25, far: 55 },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // AXES — Les 6 dimensions invariantes
  // ═══════════════════════════════════════════════════════════════════════════

  axes: [
    {
      id: "posture",
      label: "Posture face à l'incertitude",
      shortLabel: "Posture",
      description: "Comment l'acteur se situe face à ce qu'il ne contrôle pas.",
      epistemologicalQuestion: "Face à ce que je ne maîtrise pas, est-ce que je serre ou est-ce que j'écoute ?",
      poleA: {
        id: "controle",
        label: "Contrôle",
        shortLabel: "CTRL",
        description: "Chercher à maîtriser, prévoir, réduire l'incertitude",
        phenomenology: "L'incertitude est vécue comme une menace à réduire.",
        color: "#ef4444",
        consequences: {
          court: ["Sentiment de maîtrise", "Réduction de l'anxiété immédiate"],
          long: ["Épuisement", "Rigidité", "Angle mort systémique", "Fragilité au changement"],
        },
      },
      poleB: {
        id: "navigation",
        label: "Navigation",
        shortLabel: "NAV",
        description: "Composer avec, s'ajuster, lire les signaux",
        phenomenology: "L'incertitude est vécue comme un milieu à habiter.",
        color: "#22d3ee",
        consequences: {
          court: ["Inconfort", "Doute", "Lenteur apparente"],
          long: ["Adaptabilité", "Vigilance", "Ouverture", "Robustesse"],
        },
      },
      theoryRefs: ["concept_incertitude_vivable", "concept_navigation_vs_controle"],
    },
    {
      id: "temps",
      label: "Rapport au temps",
      shortLabel: "Temps",
      description: "L'horizon temporel dans lequel l'acteur pense et agit.",
      epistemologicalQuestion: "Est-ce que je corrige maintenant, ou est-ce que je cultive ?",
      poleA: {
        id: "court_terme",
        label: "Court terme",
        shortLabel: "CT",
        description: "Réagir, corriger, optimiser dans l'instant",
        phenomenology: "Le présent comme urgence permanente.",
        color: "#f97316",
        consequences: {
          court: ["Réactivité", "Sensation de contrôle"],
          long: ["Myopie stratégique", "Volatilité", "Épuisement du sens"],
        },
      },
      poleB: {
        id: "temps_long",
        label: "Temps long",
        shortLabel: "TL",
        description: "Construire, cultiver, laisser mûrir",
        phenomenology: "Le présent comme terreau d'un futur qui n'est pas encore visible.",
        color: "#a78bfa",
        consequences: {
          court: ["Patience", "Résistance à l'urgence"],
          long: ["Cumul", "Robustesse", "Sens durable"],
        },
      },
      theoryRefs: ["concept_temps_long", "concept_cumul_vs_optimisation"],
    },
    {
      id: "autre",
      label: "Rapport à l'autre",
      shortLabel: "Autre",
      description: "La manière dont l'acteur considère et agit avec les autres.",
      epistemologicalQuestion: "Est-ce que je fais advenir l'autre, ou est-ce que je le fais entrer dans mon cadre ?",
      poleA: {
        id: "controler",
        label: "Contrôler",
        shortLabel: "CTRL",
        description: "Diriger, prescrire, encadrer",
        phenomenology: "L'autre est un moyen ou un risque.",
        color: "#ef4444",
        consequences: {
          court: ["Conformité", "Prévisibilité"],
          long: ["Dépendance", "Fragilité", "Étouffement du potentiel"],
        },
      },
      poleB: {
        id: "accompagner",
        label: "Accompagner",
        shortLabel: "ACC",
        description: "Soutenir, révéler, laisser advenir",
        phenomenology: "L'autre est un sujet dont l'autonomie est le but.",
        color: "#34d399",
        consequences: {
          court: ["Incertitude sur le résultat", "Temps long"],
          long: ["Autonomie", "Résilience", "Croissance"],
        },
      },
      theoryRefs: ["concept_accompagnement", "concept_revelation_vs_prescription"],
    },
    {
      id: "mesure",
      label: "Rapport à la mesure",
      shortLabel: "Mesure",
      description: "Ce que l'acteur cherche à faire avec les chiffres.",
      epistemologicalQuestion: "Est-ce que je mesure pour juger, ou est-ce que je mesure pour comprendre ?",
      poleA: {
        id: "evaluer",
        label: "Évaluer",
        shortLabel: "EVAL",
        description: "Mesurer pour juger, classer, décider",
        phenomenology: "Le chiffre est un verdict.",
        color: "#fbbf24",
        consequences: {
          court: ["Clarté", "Décision rapide"],
          long: ["Hiérarchie", "Pression", "Optimisation locale", "Effet Goodhart"],
        },
      },
      poleB: {
        id: "documenter",
        label: "Documenter",
        shortLabel: "DOC",
        description: "Mesurer pour comprendre, garder trace, apprendre",
        phenomenology: "Le chiffre est une trace.",
        color: "#22d3ee",
        consequences: {
          court: ["Accumulation", "Complexité apparente"],
          long: ["Compréhension", "Apprentissage cumulatif", "Sagesse systémique"],
        },
      },
      theoryRefs: ["concept_effet_goodhart", "concept_mesure_comme_langage"],
    },
    {
      id: "erreur",
      label: "Rapport à l'erreur",
      shortLabel: "Erreur",
      description: "Ce que l'acteur fait de ce qui ne fonctionne pas.",
      epistemologicalQuestion: "Est-ce que je punis, ou est-ce que j'écoute ?",
      poleA: {
        id: "sanctionner",
        label: "Sanctionner",
        shortLabel: "SANCT",
        description: "Punir, corriger, éviter",
        phenomenology: "L'erreur est une faute.",
        color: "#ef4444",
        consequences: {
          court: ["Ordre apparent", "Dissuasion"],
          long: ["Dissimulation", "Répétition", "Appauvrissement", "Cécité aux signaux faibles"],
        },
      },
      poleB: {
        id: "accueillir",
        label: "Accueillir",
        shortLabel: "ACC",
        description: "Écouter, comprendre, intégrer",
        phenomenology: "L'erreur est un signal.",
        color: "#34d399",
        consequences: {
          court: ["Désordre apparent", "Vulnérabilité"],
          long: ["Apprentissage", "Innovation", "Robustesse"],
        },
      },
      theoryRefs: ["concept_erreur_ressource", "concept_signal_faible"],
    },
    {
      id: "systeme",
      label: "Rapport au système",
      shortLabel: "Système",
      description: "Ce que l'acteur voit quand il regarde le monde.",
      epistemologicalQuestion: "Est-ce que je vois des choses, ou est-ce que je vois des relations ?",
      poleA: {
        id: "silos",
        label: "Silos",
        shortLabel: "SILO",
        description: "Voir des éléments séparés",
        phenomenology: "Le monde est fait d'objets indépendants.",
        color: "#94a3b8",
        consequences: {
          court: ["Clarté apparente", "Spécialisation"],
          long: ["Optimisation locale", "Conflits", "Aveuglement aux interactions"],
        },
      },
      poleB: {
        id: "interactions",
        label: "Interactions",
        shortLabel: "INT",
        description: "Voir des relations et des boucles",
        phenomenology: "Le monde est fait de relations.",
        color: "#a78bfa",
        consequences: {
          court: ["Complexité", "Doute sur les actions"],
          long: ["Compréhension systémique", "Coordination", "Cumul"],
        },
      },
      theoryRefs: ["concept_pensee_systemique", "concept_boucles_retroaction"],
    },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // ARCHÉTYPES
  // ═══════════════════════════════════════════════════════════════════════════

  archetypes: [
    {
      id: "architecte",
      label: "L'Architecte",
      shortLabel: "ARCH",
      icon: "◇",
      description: "Conçoit des systèmes qui tiennent dans le temps. Voit loin, accepte l'incertitude, cultive l'autonomie.",
      phenomenology: "Habite le temps long comme une évidence. L'incertitude n'est pas une menace mais le milieu même de l'action.",
      color: "#22d3ee",
      position: { posture: 0.85, temps: 0.9, autre: 0.8, mesure: 0.75, erreur: 0.8, systeme: 0.9 },
      domains: ["education", "sante", "recherche", "defense", "architecture"],
      outcomes: ["Durabilité", "Cumul", "Résilience", "Transmission"],
      blindspots: ["Peut apparaître lent dans un monde qui exige de la réactivité", "Frustration face aux systèmes qui l'ignorent"],
      signature: "Construit ce qui tient quand il n'est plus là.",
    },
    {
      id: "pilote",
      label: "Le Pilote",
      shortLabel: "PILOT",
      icon: "▲",
      description: "Agit vite, ajuste en continu. Efficace à court terme, vulnérable au changement de contexte.",
      phenomenology: "Le présent est une série de décisions à prendre. Le futur est une prolongation du présent.",
      color: "#f97316",
      position: { posture: 0.7, temps: 0.2, autre: 0.5, mesure: 0.3, erreur: 0.5, systeme: 0.5 },
      domains: ["finance", "sport", "urgence", "defense"],
      outcomes: ["Réactivité", "Performance", "Capacité d'ajustement"],
      blindspots: ["Épuisement", "Myopie", "Incapacité à cultiver"],
      signature: "Réagit juste dans l'instant, mais construit peu.",
    },
    {
      id: "controleur",
      label: "Le Contrôleur",
      shortLabel: "CTRL",
      icon: "■",
      description: "Cherche à maîtriser chaque variable. Rassurant à court terme, épuisant et fragile.",
      phenomenology: "L'incertitude est un défaut à corriger. Le monde est un ensemble de variables à piloter.",
      color: "#ef4444",
      position: { posture: 0.15, temps: 0.3, autre: 0.2, mesure: 0.2, erreur: 0.15, systeme: 0.2 },
      domains: ["management", "bureaucratie", "industrie", "militaire_classique"],
      outcomes: ["Conformité", "Prévisibilité", "Ordre apparent"],
      blindspots: ["Angle mort systémique", "Fragilité structurelle", "Épuisement", "Dissimulation des erreurs"],
      signature: "Tient tout, mais rien ne tient sans lui.",
    },
    {
      id: "gardien",
      label: "Le Gardien",
      shortLabel: "GARD",
      icon: "◆",
      description: "Protège, conserve, transmet. Sait ce qui compte, mais peut résister au changement nécessaire.",
      phenomenology: "Le temps long comme responsabilité. Ce qui a été transmis mérite d'être préservé.",
      color: "#a78bfa",
      position: { posture: 0.4, temps: 0.9, autre: 0.7, mesure: 0.8, erreur: 0.6, systeme: 0.6 },
      domains: ["culture", "droit", "patrimoine", "enseignement_classique"],
      outcomes: ["Continuité", "Sens", "Transmission"],
      blindspots: ["Résistance au changement nécessaire", "Confusion entre conservation et immobilisme"],
      signature: "Préserve ce qui compte, mais parfois trop longtemps.",
    },
    {
      id: "explorateur",
      label: "L'Explorateur",
      shortLabel: "EXPL",
      icon: "✦",
      description: "Cherche, teste, découvre. Crée du neuf mais peine à consolider.",
      phenomenology: "Le monde est un champ de possibles. L'erreur est un guide, non un échec.",
      color: "#34d399",
      position: { posture: 0.9, temps: 0.5, autre: 0.6, mesure: 0.7, erreur: 0.9, systeme: 0.8 },
      domains: ["recherche", "startup", "art", "renseignement"],
      outcomes: ["Innovation", "Découverte", "Ouverture"],
      blindspots: ["Dispersion", "Incapacité à consolider", "Instabilité"],
      signature: "Ouvre des voies, mais en referme peu.",
    },
    {
      id: "operateur",
      label: "L'Opérateur",
      shortLabel: "OPER",
      icon: "○",
      description: "Exécute avec rigueur dans un cadre donné. Fiable mais limité par le cadre.",
      phenomenology: "Le monde est un ensemble de procédures à respecter. La qualité se mesure à la conformité.",
      color: "#94a3b8",
      position: { posture: 0.3, temps: 0.4, autre: 0.4, mesure: 0.4, erreur: 0.4, systeme: 0.3 },
      domains: ["industrie", "sante", "logistique", "militaire_execution"],
      outcomes: ["Fiabilité", "Prévisibilité", "Qualité constante"],
      blindspots: ["Angle mort systémique", "Incapacité à voir au-delà du cadre"],
      signature: "Fait parfaitement ce qui est demandé — rien de plus.",
    },
    {
      id: "sage",
      label: "Le Sage",
      shortLabel: "SAGE",
      icon: "☉",
      description: "Comprend sans imposer. Révèle plutôt qu'enseigne, accompagne plutôt que dirige.",
      phenomenology: "Le monde est un tissu de relations. L'action juste est souvent l'action minimale.",
      color: "#fbbf24",
      position: { posture: 0.95, temps: 0.95, autre: 0.9, mesure: 0.7, erreur: 0.95, systeme: 0.95 },
      domains: ["education", "sante", "spiritualite", "accompagnement"],
      outcomes: ["Transmission", "Autonomie", "Durabilité"],
      blindspots: ["Peut paraître inactif dans un monde qui valorise l'action visible"],
      signature: "Rend l'autre capable de se passer de lui.",
    },
    {
      id: "oracle",
      label: "L'Oracle",
      shortLabel: "ORCL",
      icon: "◈",
      description: "Voit les trajectoires, anticipe les tensions, mais peut rester dans la contemplation sans agir.",
      phenomenology: "Le monde est un système dont on peut lire les lignes de force. L'action importe moins que la vision.",
      color: "#818cf8",
      position: { posture: 0.85, temps: 0.9, autre: 0.5, mesure: 0.8, erreur: 0.7, systeme: 0.95 },
      domains: ["recherche", "prospective", "strategie", "renseignement"],
      outcomes: ["Vision", "Anticipation", "Compréhension profonde"],
      blindspots: ["Paralysie par la lucidité", "Distance à l'action", "Solitude"],
      signature: "Voit loin, mais touche peu.",
    },
    {
      id: "artisan",
      label: "L'Artisan",
      shortLabel: "ARTS",
      icon: "◐",
      description: "Fait bien, avec soin, dans un périmètre maîtrisé. La qualité comme éthique, non comme performance.",
      phenomenology: "L'action juste se reconnaît à sa texture. Le temps est un allié, pas un ennemi.",
      color: "#fb923c",
      position: { posture: 0.6, temps: 0.7, autre: 0.6, mesure: 0.6, erreur: 0.7, systeme: 0.5 },
      domains: ["artisanat", "sante", "cuisine", "art"],
      outcomes: ["Qualité", "Durabilité", "Sens du travail"],
      blindspots: ["Peut manquer d'ambition systémique", "Difficulté à déléguer"],
      signature: "Fait peu, mais bien — et le temps lui donne raison.",
    },
    {
      id: "agitateur",
      label: "L'Agitateur",
      shortLabel: "AGIT",
      icon: "✱",
      description: "Bouscule les systèmes figés, crée du mouvement. Nécessaire mais parfois destructeur.",
      phenomenology: "Le monde est un ordre à déranger. L'énergie est la réponse à la paralysie.",
      color: "#f43f5e",
      position: { posture: 0.75, temps: 0.25, autre: 0.4, mesure: 0.3, erreur: 0.7, systeme: 0.5 },
      domains: ["activisme", "startup", "politique", "art_engage"],
      outcomes: ["Mouvement", "Révélation des tensions", "Déblocage"],
      blindspots: ["Peut tout casser sans reconstruire", "Épuisement", "Isolement"],
      signature: "Révèle ce qui devait être vu — au prix du confort.",
    },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // SCÉNARIOS — Situations décisionnelles typées
  // Chaque scénario est générique mais décline des projections par domaine.
  // ═══════════════════════════════════════════════════════════════════════════

  scenarios: [
    {
      id: "scenario_incertitude_temporelle",
      label: "Décision sous pression temporelle",
      shortLabel: "INCERT. TEMPS",
      description: "Information incomplète, temps contraint, conséquences irréversibles.",
      context: "Vous disposez de 30 secondes. Vous ne savez pas tout. Ce que vous décidez engage.",
      epistemologicalQuestion: "Que faites-vous de ce que vous ne savez pas ?",
      dimensions: ["temps", "posture", "erreur"],
      behaviors: {
        controleur: {
          behavior: "Cherche plus d'information au lieu d'agir",
          outcome: "Dépassement du délai, décision subie",
          risk: "critical",
        },
        pilote: {
          behavior: "Agit sur l'information disponible",
          outcome: "Décision prise dans les délais",
          risk: "moderate",
        },
        architecte: {
          behavior: "Reconnaît la limite de l'information, décide et ajuste",
          outcome: "Décision assumée, révisable",
          risk: "low",
        },
        oracle: {
          behavior: "Voit tous les scénarios possibles, mais tarde à trancher",
          outcome: "Paralysie par la lucidité",
          risk: "critical",
        },
        sage: {
          behavior: "Décide peu, laisse le temps agir si possible",
          outcome: "Décision parfois trop tardive dans l'urgence",
          risk: "moderate",
        },
        operateur: {
          behavior: "Applique la procédure prévue",
          outcome: "Décision conforme, parfois inadaptée",
          risk: "moderate",
        },
        explorateur: {
          behavior: "Teste une option nouvelle",
          outcome: "Innovation ou échec rapide",
          risk: "moderate",
        },
        gardien: {
          behavior: "Préserve ce qui existe, attend",
          outcome: "Décision conservatrice, parfois salvatrice",
          risk: "moderate",
        },
        artisan: {
          behavior: "Fait ce qu'il sait faire, proprement",
          outcome: "Décision solide dans son périmètre",
          risk: "low",
        },
        agitateur: {
          behavior: "Bouscule, tranche, avance",
          outcome: "Décision rapide mais parfois mal fondée",
          risk: "moderate",
        },
      },
      criticalPostures: ["controleur", "oracle"],
      durablePostures: ["architecte", "pilote", "artisan"],
      projections: {
        medicale: {
          label: "Triage d'urgence",
          example: "30 secondes pour décider qui passe en priorité aux urgences.",
        },
        militaire: {
          label: "Décision tactique sous feu",
          example: "30 secondes pour engager une manœuvre sous pression ennemie.",
        },
        juridique: {
          label: "Décision en référé",
          example: "30 secondes pour accorder ou refuser une mesure d'urgence.",
        },
        financiere: {
          label: "Arbitrage sous volatilité",
          example: "30 secondes pour couper une position sous stress de marché.",
        },
        educative: {
          label: "Évaluation en temps contraint",
          example: "30 secondes pour décider du passage d'un élève en difficulté.",
        },
        industrielle: {
          label: "Arrêt d'urgence",
          example: "30 secondes pour arrêter ou non une chaîne de production.",
        },
      },
      theoryRefs: ["concept_incertitude_vivable", "concept_navigation_vs_controle"],
    },
    {
      id: "scenario_information_incomplete",
      label: "Décision avec information incomplète",
      shortLabel: "INFO INCOMPL.",
      description: "Il manque un élément, mais il ne viendra jamais. Il faut décider avec ce qu'on a.",
      context: "Vous savez qu'il vous manque quelque chose d'important. Attendre est impossible.",
      epistemologicalQuestion: "Comment décider sans savoir tout ce dont j'aurais besoin ?",
      dimensions: ["posture", "mesure", "systeme"],
      behaviors: {
        controleur: {
          behavior: "Cherche à réduire le manque par plus de données",
          outcome: "Recherche infinie, décision jamais prise",
          risk: "critical",
        },
        oracle: {
          behavior: "Comble le manque par interprétation",
          outcome: "Décision plausible mais non fondée",
          risk: "moderate",
        },
        architecte: {
          behavior: "Décide avec ce qu'il a, prévoit des ajustements",
          outcome: "Décision évolutive",
          risk: "low",
        },
        sage: {
          behavior: "Attend la maturation des signaux disponibles",
          outcome: "Décision parfois juste, parfois trop lente",
          risk: "moderate",
        },
        explorateur: {
          behavior: "Décide en pariant sur l'inconnu",
          outcome: "Décision créative mais risquée",
          risk: "moderate",
        },
        operateur: {
          behavior: "Complète avec la procédure standard",
          outcome: "Décision rigoureuse mais aveugle au contexte",
          risk: "moderate",
        },
      },
      criticalPostures: ["controleur", "oracle"],
      durablePostures: ["architecte", "pilote", "explorateur"],
      projections: {
        medicale: {
          label: "Diagnostic en absence d'examen",
          example: "Traiter sans imagerie, faute de temps.",
        },
        militaire: {
          label: "Décision renseignement partiel",
          example: "Engager sans connaître les effectifs adverses.",
        },
        juridique: {
          label: "Jugement en absence de preuve",
          example: "Décider au bénéfice du doute.",
        },
        financiere: {
          label: "Position sans information complète",
          example: "Investir sur un actif opaque.",
        },
        educative: {
          label: "Orientation sans évaluation complète",
          example: "Décider d'une filière pour un élève en cours d'évaluation.",
        },
      },
      theoryRefs: ["concept_incertitude_vivable", "concept_signal_faible"],
    },
    {
      id: "scenario_conflit_valeurs",
      label: "Décision avec conflit de valeurs",
      shortLabel: "CONFLIT VAL.",
      description: "Chaque option a une légitimité. Chaque choix trahit quelque chose.",
      context: "Vous devez choisir entre deux biens qui s'excluent. Aucun des deux n'est mauvais.",
      epistemologicalQuestion: "Comment décider quand tout choix est une perte ?",
      dimensions: ["autre", "erreur", "systeme"],
      behaviors: {
        controleur: {
          behavior: "Cherche la règle qui tranche",
          outcome: "Décision cohérente mais parfois inhumaine",
          risk: "moderate",
        },
        sage: {
          behavior: "Prend acte de l'irréductibilité, choisit avec conscience",
          outcome: "Décision assumée, portée dans le temps",
          risk: "low",
        },
        architecte: {
          behavior: "Cherche une solution qui préserve les deux dimensions",
          outcome: "Décision parfois impossible",
          risk: "moderate",
        },
        pilote: {
          behavior: "Tranche rapidement",
          outcome: "Décision claire mais potentiellement violente",
          risk: "moderate",
        },
        gardien: {
          behavior: "Préserve la valeur la plus ancienne",
          outcome: "Décision légitime mais conservatrice",
          risk: "moderate",
        },
        agitateur: {
          behavior: "Retourne la question, force à choisir autrement",
          outcome: "Décision dérangeante mais parfois juste",
          risk: "moderate",
        },
      },
      criticalPostures: ["controleur"],
      durablePostures: ["sage", "architecte", "artisan"],
      projections: {
        medicale: {
          label: "Choix entre deux patients",
          example: "Un seul lit de réanimation pour deux urgences.",
        },
        militaire: {
          label: "Choix entre mission et vies",
          example: "Sacrifier l'unité pour réussir la mission.",
        },
        juridique: {
          label: "Choix entre légalité et équité",
          example: "Appliquer la loi à la lettre ou l'esprit.",
        },
        financiere: {
          label: "Choix entre performance et éthique",
          example: "Investir dans un actif rentable mais nuisible.",
        },
        educative: {
          label: "Choix entre exigence et bienveillance",
          example: "Sanctionner ou accompagner un élève en difficulté.",
        },
      },
      theoryRefs: ["concept_navigation_vs_controle", "concept_erreur_ressource"],
    },
    {
      id: "scenario_enjeu_humain",
      label: "Décision avec enjeu humain direct",
      shortLabel: "ENJEU HUMAIN",
      description: "Derrière la décision, il y a des personnes. Leurs vies en dépendent.",
      context: "Ce n'est plus un problème abstrait. Une personne réelle est concernée.",
      epistemologicalQuestion: "Comment tenir la rigueur quand l'humain est en jeu ?",
      dimensions: ["autre", "erreur", "posture"],
      behaviors: {
        controleur: {
          behavior: "Applique la règle pour protéger la décision",
          outcome: "Décision défendable, parfois froide",
          risk: "moderate",
        },
        artisan: {
          behavior: "Traite chaque cas dans sa singularité",
          outcome: "Décision juste, coûteuse en énergie",
          risk: "moderate",
        },
        sage: {
          behavior: "Accompagne la personne dans la décision",
          outcome: "Décision partagée, durable",
          risk: "low",
        },
        pilote: {
          behavior: "Décide vite pour ne pas faire attendre",
          outcome: "Décision efficace, parfois précipitée",
          risk: "moderate",
        },
        operateur: {
          behavior: "Suit le protocole",
          outcome: "Décision sûre mais parfois déshumanisante",
          risk: "moderate",
        },
        architecte: {
          behavior: "Conçoit un cadre qui protège l'humain dans la durée",
          outcome: "Décision systémique, parfois lente",
          risk: "low",
        },
      },
      criticalPostures: ["controleur"],
      durablePostures: ["sage", "artisan", "architecte"],
      projections: {
        medicale: {
          label: "Annonce d'un diagnostic grave",
          example: "Dire la vérité au patient et à sa famille.",
        },
        militaire: {
          label: "Annonce d'une perte",
          example: "Informer une famille d'un décès au combat.",
        },
        juridique: {
          label: "Décision affectant une famille",
          example: "Placement d'un enfant en danger.",
        },
        educative: {
          label: "Orientation d'un élève vulnérable",
          example: "Décider d'un redoublement lourd de conséquences.",
        },
      },
      theoryRefs: ["concept_accompagnement", "concept_revelation_vs_prescription"],
    },
    {
      id: "scenario_pression_politique",
      label: "Décision sous pression institutionnelle",
      shortLabel: "PRESS. INSTIT.",
      description: "Ce qui est attendu n'est pas ce qui est juste. Tenir sa posture a un coût.",
      context: "Votre décision sera scrutée par une hiérarchie, un public, ou une institution.",
      epistemologicalQuestion: "Comment décider droit quand le contexte pousse à pencher ?",
      dimensions: ["posture", "mesure", "autre"],
      behaviors: {
        controleur: {
          behavior: "Anticipe ce qui sera jugé, s'aligne",
          outcome: "Décision conforme, potentiellement erronée",
          risk: "critical",
        },
        gardien: {
          behavior: "S'appuie sur le cadre, résiste à la pression",
          outcome: "Décision stable, parfois en conflit",
          risk: "moderate",
        },
        architecte: {
          behavior: "Décide selon les principes, accepte les conséquences",
          outcome: "Décision durable, parfois isolée",
          risk: "moderate",
        },
        oracle: {
          behavior: "Voit les jeux de pouvoir, peut y céder",
          outcome: "Décision tacticienne",
          risk: "moderate",
        },
        agitateur: {
          behavior: "Affronte la pression frontalement",
          outcome: "Décision tranchante, parfois coûteuse",
          risk: "moderate",
        },
        sage: {
          behavior: "Décide selon sa conscience, sans bruit",
          outcome: "Décision juste et silencieuse",
          risk: "low",
        },
      },
      criticalPostures: ["controleur", "oracle"],
      durablePostures: ["sage", "architecte", "gardien"],
      projections: {
        medicale: {
          label: "Pression pour un lit",
          example: "Un chef de service demande un passe-droit.",
        },
        militaire: {
          label: "Pression hiérarchique sur une décision",
          example: "Un ordre contestable remonté par la voie normale.",
        },
        juridique: {
          label: "Pression médiatique sur une affaire",
          example: "Un jugement attendu par l'opinion.",
        },
        educative: {
          label: "Pression d'un parent influent",
          example: "Une note contestée par une famille puissante.",
        },
        financiere: {
          label: "Pression pour un résultat trimestriel",
          example: "Prendre un risque excessif pour tenir les objectifs.",
        },
      },
      theoryRefs: ["concept_navigation_vs_controle", "concept_effet_goodhart"],
    },
    {
      id: "scenario_retour_experience",
      label: "Décision à partir d'un retour d'expérience",
      shortLabel: "RETEX",
      description: "Ce qui s'est passé doit nourrir ce qui va se passer. Sinon, l'échec se répète.",
      context: "Un événement passé demande à être compris, pas seulement à être jugé.",
      epistemologicalQuestion: "Que fait-on de ce qui a mal tourné ?",
      dimensions: ["erreur", "mesure", "systeme"],
      behaviors: {
        controleur: {
          behavior: "Cherche le responsable",
          outcome: "Dissimulation, répétition de l'erreur",
          risk: "critical",
        },
        operateur: {
          behavior: "Applique la procédure de retour standard",
          outcome: "RETEX formel, peu nourrissant",
          risk: "moderate",
        },
        architecte: {
          behavior: "Analyse le système, pas les personnes",
          outcome: "RETEX systémique, apprentissage durable",
          risk: "low",
        },
        sage: {
          behavior: "Écoute les silences, les non-dits",
          outcome: "RETEX profond, parfois inconfortable",
          risk: "low",
        },
        explorateur: {
          behavior: "Transforme l'échec en piste nouvelle",
          outcome: "RETEX créatif, parfois hors-sujet",
          risk: "moderate",
        },
        oracle: {
          behavior: "Relie l'événement à des tendances de fond",
          outcome: "RETEX prospectif, parfois déconnecté",
          risk: "moderate",
        },
      },
      criticalPostures: ["controleur"],
      durablePostures: ["architecte", "sage", "explorateur"],
      projections: {
        medicale: {
          label: "Analyse de morbi-mortalité",
          example: "Comprendre un événement indésirable grave sans chercher un coupable.",
        },
        militaire: {
          label: "RETEX opérationnel",
          example: "Analyser une mission ratée sans stigmatiser l'unité.",
        },
        educative: {
          label: "Analyse d'échec scolaire",
          example: "Comprendre pourquoi un élève décroche.",
        },
        financiere: {
          label: "Post-mortem de trading",
          example: "Analyser une perte majeure sans juger le trader.",
        },
        industrielle: {
          label: "Analyse d'accident industriel",
          example: "Comprendre la chaîne causale sans désigner un fautif.",
        },
      },
      theoryRefs: ["concept_erreur_ressource", "concept_pensee_systemique"],
    },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // DOMAINES — Projections contextuelles (universaliste et agnostique)
  // ═══════════════════════════════════════════════════════════════════════════

  domains: [
    {
      id: "medicale",
      label: "Médical",
      color: "#ef4444",
      actors: ["soignant", "medecin", "patient", "famille", "institution"],
      contexts: ["clinique", "urgence", "soins_palliatifs", "sante_publique"],
      referentialHints: ["referentiel_medical", "certification_clinique"],
    },
    {
      id: "militaire",
      label: "Défense",
      color: "#22d3ee",
      actors: ["operateur", "instructeur", "commandant", "auditeur", "autorite_politique"],
      contexts: ["preparation_operationnelle", "formation_continue", "retour_experience", "commandement"],
      referentialHints: ["referentiel_decisions_ops", "certification_ops"],
    },
    {
      id: "educative",
      label: "Éducation",
      color: "#34d399",
      actors: ["enseignant", "eleve", "formateur", "institution", "parent"],
      contexts: ["classe", "accompagnement", "orientation", "formation_adultes"],
      referentialHints: ["referentiel_education", "competences_cles"],
    },
    {
      id: "juridique",
      label: "Droit",
      color: "#a78bfa",
      actors: ["juge", "avocat", "justiciable", "greffe", "institution"],
      contexts: ["audience", "instruction", "arbitrage", "mediation"],
      referentialHints: ["referentiel_juridique"],
    },
    {
      id: "financiere",
      label: "Finance",
      color: "#f97316",
      actors: ["trader", "risk_manager", "analyste", "client", "regulateur"],
      contexts: ["marche", "gestion_risque", "arbitrage", "conformite"],
      referentialHints: ["referentiel_financier"],
    },
    {
      id: "industrielle",
      label: "Industrie",
      color: "#fbbf24",
      actors: ["operateur", "superviseur", "technicien", "direction", "inspecteur"],
      contexts: ["production", "maintenance", "securite", "qualite"],
      referentialHints: ["referentiel_industriel"],
    },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // ACTEURS
  // ═══════════════════════════════════════════════════════════════════════════

  actors: [
    {
      id: "self",
      label: "Vous",
      type: "self",
      color: "#00e5ff",
      editable: true,
      position: { posture: 0.5, temps: 0.5, autre: 0.5, mesure: 0.5, erreur: 0.5, systeme: 0.5 },
    },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // TRAJECTOIRES
  // ═══════════════════════════════════════════════════════════════════════════

  trajectories: [
    {
      id: "controle_vers_navigation",
      from: "controleur",
      to: "architecte",
      label: "Du contrôle à la navigation",
      description: "Apprendre à composer avec l'incertitude au lieu de la combattre.",
      color: "#22d3ee",
      conditions: [
        "Accepter l'inconfort de ne pas tout maîtriser",
        "Pratiquer l'écoute active (signaux faibles)",
        "Créer des espaces d'expérimentation où l'erreur est permise",
        "Observer les systèmes dans leur temporalité propre",
      ],
      obstacles: [
        "Peur de perdre le contrôle (identité professionnelle)",
        "Culture organisationnelle qui valorise la maîtrise",
        "Habitude cognitive (le contrôle rassure immédiatement)",
        "Pression de performance à court terme",
      ],
      stages: [
        { name: "Reconnaissance", description: "Voir que le contrôle a un coût" },
        { name: "Expérimentation", description: "Tester la navigation dans un périmètre sûr" },
        { name: "Intégration", description: "Faire de la navigation une posture par défaut" },
      ],
      duration: "mois à années",
      theoryRefs: ["concept_navigation_vs_controle"],
    },
    {
      id: "court_vers_long",
      from: "pilote",
      to: "architecte",
      label: "Élargir l'horizon temporel",
      description: "Passer de la réaction à la construction.",
      color: "#a78bfa",
      conditions: [
        "Créer des rituels de recul (hebdomadaires, trimestriels)",
        "Documenter les décisions et leurs effets différés",
        "Mesurer ce qui compte sur le long terme, pas seulement ce qui se voit",
        "Accepter de ne pas voir les résultats immédiatement",
      ],
      obstacles: [
        "Pression de performance court terme",
        "Cycle de récompense immédiat",
        "Difficulté à valoriser ce qui n'est pas encore visible",
      ],
      stages: [
        { name: "Décentrement", description: "Prendre conscience de la myopie" },
        { name: "Rituels", description: "Installer des temps de recul réguliers" },
        { name: "Cumul", description: "Voir les effets des décisions longues s'accumuler" },
      ],
      duration: "années",
      theoryRefs: ["concept_temps_long"],
    },
    {
      id: "sanction_vers_accueil",
      from: "operateur",
      to: "explorateur",
      label: "De la sanction à l'accueil",
      description: "Passer de la correction à l'apprentissage.",
      color: "#34d399",
      conditions: [
        "Créer des espaces de parole sans jugement",
        "Pratiquer les post-mortems où l'on cherche à comprendre, pas à désigner",
        "Célébrer les apprentissages, pas seulement les succès",
        "Distinguer l'erreur d'exécution de l'erreur de conception",
      ],
      obstacles: [
        "Culture de la faute (héritage scolaire, industriel)",
        "Peur du jugement social",
        "Cadres juridiques qui individualisent la responsabilité",
      ],
      stages: [
        { name: "Désamorçage", description: "Nommer la peur de l'erreur" },
        { name: "Rituels", description: "Installer des temps d'analyse bienveillante" },
        { name: "Culture", description: "Faire de l'erreur une ressource collective" },
      ],
      duration: "mois à années",
      theoryRefs: ["concept_erreur_ressource"],
    },
    {
      id: "silos_vers_systeme",
      from: "operateur",
      to: "architecte",
      label: "Des silos au système",
      description: "Apprendre à voir les relations, pas seulement les éléments.",
      color: "#a78bfa",
      conditions: [
        "Cartographier les interactions, pas seulement les acteurs",
        "Chercher les boucles de rétroaction",
        "Comprendre que les effets sont souvent décalés",
        "Accepter la complexité comme donnée, non comme problème à résoudre",
      ],
      obstacles: [
        "Formation disciplinaire (pensée en silos)",
        "Organisation en départements séparés",
        "Récompense de l'optimisation locale",
      ],
      stages: [
        { name: "Reconnaissance", description: "Voir que les silos produisent des angles morts" },
        { name: "Cartographie", description: "Apprendre à dessiner les relations" },
        { name: "Systémique", description: "Penser en boucles par défaut" },
      ],
      duration: "années",
      theoryRefs: ["concept_pensee_systemique"],
    },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // TENSIONS SYSTÉMIQUES
  // ═══════════════════════════════════════════════════════════════════════════

  tensions: [
    {
      id: "tension_controle_temps_long",
      between: ["posture", "temps"],
      description: "Le contrôle exige une réponse immédiate. Le temps long demande de la patience.",
      example: "Un manager qui veut tout maîtriser ne peut pas cultiver un projet à 5 ans.",
      resolution: "Distinguer les zones où le contrôle est nécessaire et celles où la patience est féconde.",
    },
    {
      id: "tension_mesure_accompagnement",
      between: ["mesure", "autre"],
      description: "Évaluer produit de la hiérarchie. Accompagner produit de l'autonomie.",
      example: "Un enseignant qui note produit de la conformité, celui qui documente produit de l'apprentissage.",
      resolution: "Séparer les temps d'évaluation des temps d'accompagnement.",
    },
    {
      id: "tension_erreur_systeme",
      between: ["erreur", "systeme"],
      description: "Sanctionner l'erreur cache les signaux faibles. Voir le système exige de les accueillir.",
      example: "Une organisation qui punit les erreurs ne voit plus les signaux qui annoncent les crises.",
      resolution: "Créer des espaces protégés où l'erreur est une donnée, pas une faute.",
    },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // INTERACTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  interactions: [
    {
      id: "interaction_self_position",
      objectId: "self",
      label: "Me positionner",
      description: "Où est-ce que je me situe aujourd'hui sur chaque axe ?",
      onClick: { action: "open_positioning_editor" },
    },
    {
      id: "interaction_archetype_architecte",
      objectId: "architecte",
      label: "Explorer l'Architecte",
      description: "Une posture qui conjugue navigation, temps long, accompagnement et système.",
      theoryRef: "posture_architecte",
      onClick: { showArchetype: "architecte" },
    },
    {
      id: "interaction_archetype_sage",
      objectId: "sage",
      label: "Explorer le Sage",
      description: "Comprend sans imposer. Révèle plutôt qu'enseigne.",
      theoryRef: "posture_sage",
      onClick: { showArchetype: "sage" },
    },
    {
      id: "interaction_trajectoire_controle",
      objectId: "controle_vers_navigation",
      label: "Trajectoire du contrôle à la navigation",
      description: "Que faudrait-il pour passer du contrôle à la navigation ?",
      onClick: { showTrajectory: "controle_vers_navigation" },
    },
    {
      id: "interaction_tension_controle_temps",
      objectId: "tension_controle_temps_long",
      label: "Tension contrôle / temps long",
      description: "Le contrôle exige une réponse immédiate, le temps long demande de la patience.",
      onClick: { showTension: "tension_controle_temps_long" },
    },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // SIMULATIONS
  // ═══════════════════════════════════════════════════════════════════════════

  simulations: [
    {
      id: "sim_controle_vers_navigation",
      label: "Et si je passais de contrôle à navigation ?",
      from: { posture: 0.15 },
      to: { posture: 0.85 },
      effects: [
        { domain: "quotidien", change: "Moins d'épuisement, plus d'attention aux signaux", timeHorizon: "semaines" },
        { domain: "decisions", change: "Moins de rigidité, plus d'ajustement", timeHorizon: "mois" },
        { domain: "relations", change: "Moins de dépendance, plus d'autonomie autour de soi", timeHorizon: "mois" },
        { domain: "systeme", change: "Moins d'angles morts, plus de compréhension", timeHorizon: "années" },
      ],
      warnings: [
        "L'inconfort initial est réel et souvent décourageant",
        "L'entourage peut résister à ce changement",
        "Il faut un cadre sûr pour expérimenter",
      ],
    },
    {
      id: "sim_court_vers_long",
      label: "Et si j'élargissais mon horizon temporel ?",
      from: { temps: 0.2 },
      to: { temps: 0.9 },
      effects: [
        { domain: "priorites", change: "Moins d'urgences, plus de construction", timeHorizon: "mois" },
        { domain: "mesure", change: "Moins de KPIs immédiats, plus d'indicateurs de trajectoire", timeHorizon: "mois" },
        { domain: "resultats", change: "Moins de performance court terme, plus de cumul", timeHorizon: "années" },
      ],
      warnings: [
        "Le court terme ne disparaît pas — il faut le gérer autrement",
        "Les résultats ne seront pas visibles immédiatement",
      ],
    },
    {
      id: "sim_sanction_vers_accueil",
      label: "Et si je transformais l'erreur en ressource ?",
      from: { erreur: 0.15 },
      to: { erreur: 0.85 },
      effects: [
        { domain: "securite_psychologique", change: "Plus de remontée d'information", timeHorizon: "semaines" },
        { domain: "apprentissage", change: "Capitalisation sur les échecs", timeHorizon: "mois" },
        { domain: "innovation", change: "Expérimentation possible", timeHorizon: "mois" },
      ],
      warnings: [
        "Cela ne signifie pas tolérer l'incompétence",
        "Il faut distinguer erreur d'exécution et négligence",
      ],
    },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // TRAÇABILITÉ — Schéma de trace de session
  // PESACORE ne porte pas la sémantique des référentiels (chaînes opaques)
  // ═══════════════════════════════════════════════════════════════════════════

  traceability: {
    schemaVersion: "1.0",

    sessionFields: [
      { id: "session_id", type: "uuid", required: true, description: "Identifiant unique de la session" },
      { id: "actor_id", type: "string", required: true, description: "Identifiant de l'acteur (anonymisable)" },
      { id: "actor_label", type: "string", required: false, description: "Libellé affiché" },
      { id: "timestamp_start", type: "iso8601", required: true },
      { id: "timestamp_end", type: "iso8601", required: false },
      { id: "referential", type: "string", required: false, description: "Référentiel de certification (chaîne opaque)" },
      { id: "profile", type: "enum", values: ["apprenant", "formateur", "auditeur"], required: true },
      { id: "domain", type: "enum", values: ["medicale", "militaire", "educative", "juridique", "financiere", "industrielle", "dual"], required: false },
      { id: "notes", type: "string", required: false },
    ],

    trajectoryFields: [
      { id: "position_initial", type: "position6D", required: true },
      { id: "position_final", type: "position6D", required: false },
      { id: "positions_history", type: "array<position6D+timestamp>", required: false },
      { id: "trajectory_id", type: "string", required: false },
      { id: "stages_reached", type: "array<string>", required: false },
      { id: "duration_seconds", type: "number", required: false },
      { id: "scenarios_tested", type: "array<scenarioResult>", required: false },
    ],

    scenarioResult: {
      scenario_id: "string",
      timestamp: "iso8601",
      posture_before: "position6D",
      posture_after: "position6D",
      chosen_behavior: "string",
      outcome: "enum: success | moderate | failure",
      observations: "array<string>",
    },

    evidenceFields: [
      { id: "competency_ref", type: "string", required: false, description: "Référence de compétence (chaîne opaque)" },
      { id: "level_achieved", type: "enum", values: ["novice", "intermediaire", "avance", "expert"], required: false },
      { id: "observations", type: "array<string>", required: false },
      { id: "certification_ready", type: "boolean", required: false },
      { id: "signature", type: "string", required: false, description: "Empreinte optionnelle pour intégrité" },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PROFILS UTILISATEUR — Vues sur les mêmes données
  // ═══════════════════════════════════════════════════════════════════════════

  profiles: [
    {
      id: "apprenant",
      label: "Apprenant",
      description: "Explore, se positionne, joue des scénarios.",
      visibleSections: ["axes", "archetypes", "trajectories", "scenarios", "simulations"],
      editable: ["self"],
      canExport: false,
      canAnnotate: false,
      canCertify: false,
    },
    {
      id: "formateur",
      label: "Formateur / Instructeur",
      description: "Accompagne, annote, prépare des sessions.",
      visibleSections: ["axes", "archetypes", "trajectories", "scenarios", "tensions", "simulations", "trace"],
      editable: ["self", "learners"],
      canExport: true,
      canAnnotate: true,
      canCertify: false,
    },
    {
      id: "auditeur",
      label: "Auditeur / Certificateur",
      description: "Analyse les traces, produit des preuves d'acquisition.",
      visibleSections: ["trace", "evidence", "certification"],
      editable: [],
      canExport: true,
      canAnnotate: true,
      canCertify: true,
    },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // PONT INTER-DOMAINES — Correspondances civil / militaire / autres
  // ═══════════════════════════════════════════════════════════════════════════

  domainMapping: {
    sharedPostures: ["architecte", "pilote", "explorateur", "sage", "artisan", "gardien"],
    sharedScenarios: [
      "scenario_incertitude_temporelle",
      "scenario_information_incomplete",
      "scenario_conflit_valeurs",
      "scenario_enjeu_humain",
      "scenario_pression_politique",
      "scenario_retour_experience",
    ],
    bridges: [
      {
        id: "bridge_medical_militaire",
        from: "medicale",
        to: "militaire",
        label: "Médecine de guerre",
        description: "Mêmes postures, contexte de dégradation extrême.",
        sharedScenarios: ["scenario_incertitude_temporelle", "scenario_enjeu_humain"],
      },
      {
        id: "bridge_militaire_educative",
        from: "militaire",
        to: "educative",
        label: "Formation et instruction",
        description: "Mêmes enjeux de transmission, cadres institutionnels différents.",
        sharedScenarios: ["scenario_retour_experience", "scenario_conflit_valeurs"],
      },
      {
        id: "bridge_juridique_militaire",
        from: "juridique",
        to: "militaire",
        label: "Droit des conflits",
        description: "Décision juridique en contexte opérationnel.",
        sharedScenarios: ["scenario_conflit_valeurs", "scenario_pression_politique"],
      },
      {
        id: "bridge_financiere_militaire",
        from: "financiere",
        to: "militaire",
        label: "Décision sous incertitude",
        description: "Structure décisionnelle identique, univers de conséquences différent.",
        sharedScenarios: ["scenario_incertitude_temporelle", "scenario_information_incomplete"],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // RÉFÉRENCES THÉORIQUES
  // ═══════════════════════════════════════════════════════════════════════════

  theoryRefs: {
    concept_incertitude_vivable: {
      label: "L'incertitude comme milieu",
      author: "Edgar Morin",
      year: 1990,
      summary: "L'incertitude n'est pas un défaut à corriger mais la condition même de la connaissance.",
    },
    concept_navigation_vs_controle: {
      label: "Navigation vs contrôle",
      author: "Peter Senge",
      year: 1990,
      summary: "Les systèmes complexes se naviguent, ils ne se contrôlent pas.",
    },
    concept_temps_long: {
      label: "Le temps long",
      author: "François Jullien",
      year: 2001,
      summary: "La transformation durable passe par la patience et la maturation lente.",
    },
    concept_cumul_vs_optimisation: {
      label: "Cumul vs optimisation",
      author: "Nassim Taleb",
      year: 2012,
      summary: "L'optimisation locale détruit le cumul. Le cumul tolère l'imperfection locale.",
    },
    concept_accompagnement: {
      label: "Accompagnement",
      author: "Maela Paul",
      year: 2004,
      summary: "Accompagner, ce n'est pas prescrire — c'est rendre l'autre capable.",
    },
    concept_revelation_vs_prescription: {
      label: "Révélation vs prescription",
      author: "Socrate (via Platon)",
      year: -399,
      summary: "On ne transmet pas le savoir — on le fait advenir.",
    },
    concept_effet_goodhart: {
      label: "Loi de Goodhart",
      author: "Charles Goodhart",
      year: 1975,
      summary: "Quand une mesure devient un objectif, elle cesse d'être une bonne mesure.",
    },
    concept_mesure_comme_langage: {
      label: "La mesure comme langage",
      author: "James Scott",
      year: 1998,
      summary: "Ce qui est mesuré est ce qui devient visible — et cela transforme le monde.",
    },
    concept_erreur_ressource: {
      label: "L'erreur comme ressource",
      author: "Gaston Bachelard",
      year: 1938,
      summary: "L'erreur est un moment nécessaire de la connaissance.",
    },
    concept_signal_faible: {
      label: "Les signaux faibles",
      author: "Igor Ansoff",
      year: 1975,
      summary: "Les transformations majeures annoncent leur arrivée par des signaux faibles.",
    },
    concept_pensee_systemique: {
      label: "La pensée systémique",
      author: "Donella Meadows",
      year: 2008,
      summary: "Les systèmes produisent leurs comportements à travers leurs structures.",
    },
    concept_boucles_retroaction: {
      label: "Boucles de rétroaction",
      author: "Jay Forrester",
      year: 1961,
      summary: "Les systèmes complexes sont régis par des boucles qui amplifient ou stabilisent.",
    },
  },
};

export default posturesVisualization3DData;